import React, { useRef, useState } from "react";
import { addDays, addMonths, format, getDaysInMonth, parseISO, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCalendarActivity } from "./useCalendarActivity";
import { validDate } from "./dateSelection";

export default function DateRangeCalendar({
  from,
  to,
  onChange,
  activityUserIds,
}: {
  activityUserIds?: string[];
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(() =>
    startOfMonth(validDate(from) ? parseISO(from) : new Date()),
  );
  const activity = useCalendarActivity(
    activityUserIds,
    month.toISOString(),
    new Date(addMonths(month, 2).getTime() - 1).toISOString(),
  );
  const [hover, setHover] = useState("");
  const [focused, setFocused] = useState(from || format(new Date(), "yyyy-MM-dd"));
  const root = useRef<HTMLDivElement>(null);
  const locale = i18n?.resolvedLanguage;
  const pickingEnd = !!from && !to;
  const end = to || hover;
  const lower = end && from > end ? end : from;
  const upper = end && from > end ? from : end;
  const today = format(new Date(), "yyyy-MM-dd");
  const select = (day: string) => {
    setFocused(day);
    setHover("");
    if (pickingEnd) onChange(day < from ? day : from, day < from ? from : day);
    else onChange(day, "");
  };
  const moveFocus = (event: React.KeyboardEvent, day: string) => {
    const date = parseISO(day);
    const weekday = (date.getDay() + 6) % 7;
    const offsets: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      Home: -weekday,
      End: 6 - weekday,
    };
    const next =
      event.key in offsets
        ? addDays(date, offsets[event.key])
        : event.key === "PageUp"
          ? addMonths(date, -1)
          : event.key === "PageDown"
            ? addMonths(date, 1)
            : null;
    if (!next) return;
    event.preventDefault();
    const key = format(next, "yyyy-MM-dd");
    setFocused(key);
    const button = root.current?.querySelector<HTMLButtonElement>(`[data-date="${key}"]`);
    if (button && button.offsetParent !== null) button.focus();
    else {
      setMonth(startOfMonth(next));
      // Wait for the newly visible month to render before moving keyboard focus.
      requestAnimationFrame(() =>
        root.current?.querySelector<HTMLButtonElement>(`[data-date="${key}"]`)?.focus(),
      );
    }
  };
  return (
    <div ref={root}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label={t("dashboard.previous_month")}
          onClick={() => setMonth(addMonths(month, -1))}
          className="rounded-lg p-2 hover:bg-muted-surface focus-visible:outline-info"
        >
          <ChevronLeft size={16} />
        </button>
        <p aria-live="polite" className="text-center text-xs text-text-mid">
          {t(pickingEnd ? "dashboard.choose_end_date" : "dashboard.choose_start_date")}
        </p>
        <button
          type="button"
          aria-label={t("dashboard.next_month")}
          onClick={() => setMonth(addMonths(month, 1))}
          className="rounded-lg p-2 hover:bg-muted-surface focus-visible:outline-info"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" onMouseLeave={() => setHover("")}>
        {[0, 1].map(index => {
          const displayed = addMonths(month, index);
          const monthKey = format(displayed, "yyyy-MM");
          const start = (displayed.getDay() + 6) % 7;
          const count = getDaysInMonth(displayed);
          const tabDate = focused.startsWith(monthKey) ? focused : `${monthKey}-01`;
          return (
            <table
              key={monthKey}
              className={`w-full table-fixed border-collapse text-center text-sm ${index ? "hidden sm:table" : ""}`}
              aria-label={new Intl.DateTimeFormat(locale, {
                month: "long",
                year: "numeric",
              }).format(displayed)}
            >
              <caption className="pb-3 font-semibold text-text">
                {new Intl.DateTimeFormat(locale, {
                  month: "long",
                  year: "numeric",
                }).format(displayed)}
              </caption>
              <thead>
                <tr>
                  {Array.from({ length: 7 }, (_, day) => (
                    <th key={day} scope="col" className="pb-2 text-xs font-normal text-text-dim">
                      {new Intl.DateTimeFormat(locale, {
                        weekday: "narrow",
                      }).format(new Date(2024, 0, day + 1))}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 6 }, (_, week) => (
                  <tr key={week}>
                    {Array.from({ length: 7 }, (_, weekday) => {
                      const day = week * 7 + weekday - start + 1;
                      if (day < 1 || day > count) return <td key={weekday} className="h-9" />;
                      const date = new Date(displayed.getFullYear(), displayed.getMonth(), day);
                      const key = format(date, "yyyy-MM-dd");
                      const activityCount = activity.counts?.get(key) ?? 0;
                      const boundary = key === from || key === to;
                      const between = lower && upper && key >= lower && key <= upper;
                      return (
                        <td
                          key={weekday}
                          className={`h-9 p-0 ${between ? "bg-info/10" : ""} ${key === lower ? "rounded-l-lg" : ""} ${key === upper ? "rounded-r-lg" : ""}`}
                        >
                          <button
                            type="button"
                            data-date={key}
                            aria-label={new Intl.DateTimeFormat(locale, {
                              dateStyle: "full",
                            }).format(date)}
                            title={
                              activityCount
                                ? t("dashboard.calendar_sessions", { count: activityCount })
                                : undefined
                            }
                            aria-description={
                              activityCount
                                ? t("dashboard.calendar_sessions", { count: activityCount })
                                : undefined
                            }
                            aria-pressed={!!(boundary || between)}
                            aria-current={key === today ? "date" : undefined}
                            tabIndex={key === tabDate ? 0 : -1}
                            className={`relative h-9 w-full rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-info focus-visible:outline-offset-2 ${boundary ? "bg-info font-semibold text-white" : "text-text hover:bg-info/20"} ${key === today && !boundary ? "font-bold underline underline-offset-4" : ""}`}
                            onClick={() => select(key)}
                            onMouseEnter={() => pickingEnd && setHover(key)}
                            onFocus={() => {
                              setFocused(key);
                              if (pickingEnd) setHover(key);
                            }}
                            onKeyDown={event => moveFocus(event, key)}
                          >
                            {day}
                            {activityCount > 0 && (
                              <span
                                aria-hidden="true"
                                className="absolute inset-x-0 bottom-0.5 flex justify-center gap-0.5"
                              >
                                {Array.from({ length: Math.min(activityCount, 3) }, (_, dot) => (
                                  // biome-ignore lint/suspicious/noArrayIndexKey: Decorative dots have no identity or state.
                                  <span key={dot} className="h-1 w-1 rounded-full bg-current" />
                                ))}
                              </span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })}
      </div>
      {activityUserIds !== undefined && (
        <p className="mt-3 flex items-center gap-2 text-xs text-text-dim" role="status">
          <span aria-hidden="true" className="h-1 w-1 shrink-0 rounded-full bg-current" />
          {t(
            activity.loading
              ? "dashboard.calendar_activity_loading"
              : activity.failed
                ? "dashboard.calendar_activity_failed"
                : "dashboard.calendar_activity_legend",
          )}
        </p>
      )}
    </div>
  );
}
